<script setup>
/**
 * HowItWorks — onboarding presented as moments in a single day.
 *
 * The headline promise is "in production in less than a day" and the
 * section now embodies it: three real clock times (09:00 / 12:30 /
 * 17:00) instead of abstract "Step 1 / 2 / 3" badges, each anchored
 * by a small visual mockup taken from the moment it represents:
 *
 *  - 09:00 — a tiny terminal showing the mass-deploy install command,
 *    speaking the language of an IT admin scanning the page.
 *  - 12:30 — an agent status panel with an offline-recovery moment:
 *    one line says it's running silent (CPU + active dot), a small
 *    horizontal connectivity strip with a visible gap shows it
 *    survived a Wi-Fi drop and replayed the buffer when it came back.
 *    Speaks directly to "silently + resilient" rather than a generic
 *    "data is flowing" cue.
 *  - 17:00 — a comparative-KPI tile (Soporte vs Ventas with a 23pp
 *    productivity gap headline). The kind of view a manager actually
 *    screenshots to make a case. Speaks to "decision with real
 *    numbers" rather than "rule configured".
 *
 * A subtle hairline rail at the bottom of the section places three
 * dots aligned under each column to suggest the moments lie on the
 * same continuous timeline. No "Step 1/2/3" badges, no generic Lucide
 * circles, no decorative connecting line.
 *
 * The i18n `label` field ("Paso 1"/"Step 1"/"Βήμα 1") is retained in
 * the JSON but no longer rendered — the timestamp replaces it.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

// Connectivity strip for step 2: 20 segments, one per ~3 minutes.
// A central gap (greyed cells) represents a brief offline window
// during which the agent buffered events locally; on either side the
// connection is healthy. Encoded as 0 = offline, 1 = online.
const connectivity = [
  1, 1, 1, 1, 1, 1, 1, 1,
  0, 0, 0,
  1, 1, 1, 1, 1, 1, 1, 1, 1,
];

const steps = computed(() => [
  {
    time: t("landing.how_it_works.steps.0.time"),
    title: t("landing.how_it_works.steps.0.title"),
    description: t("landing.how_it_works.steps.0.description"),
    mockup: "terminal",
  },
  {
    time: t("landing.how_it_works.steps.1.time"),
    title: t("landing.how_it_works.steps.1.title"),
    description: t("landing.how_it_works.steps.1.description"),
    mockup: "telemetry",
  },
  {
    time: t("landing.how_it_works.steps.2.time"),
    title: t("landing.how_it_works.steps.2.title"),
    description: t("landing.how_it_works.steps.2.description"),
    mockup: "rule",
  },
]);
</script>

<template>
  <section id="how-it-works" class="py-24 bg-secondary/40 scroll-mt-20">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-16 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.how_it_works.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.how_it_works.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.how_it_works.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- 3 columns of moment + mockup + copy -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 max-w-5xl mx-auto">
        <AnimatedSection
          v-for="(step, idx) in steps"
          :key="idx"
          animation="fade-up"
          :delay="idx * 150"
        >
          <div class="flex flex-col">
            <!-- Mockup: on desktop sits first (mb-7 to separate from
                 the timestamp + copy below). On mobile we flip the
                 order with `order-last` so the visitor reads the
                 timestamp + title + description FIRST and then sees
                 the mockup as the concrete illustration of what was
                 just described. `mt-5 md:mt-0` adds breathing room
                 above the bumped-down mockup on mobile. -->
            <div class="h-44 flex items-stretch order-last md:order-none mt-5 md:mt-0 md:mb-7">
              <!-- Step 1 — terminal mockup with a real-looking install line -->
              <div
                v-if="step.mockup === 'terminal'"
                class="w-full rounded-lg overflow-hidden border border-zinc-700/60"
                :style="{
                  background: 'linear-gradient(180deg, hsl(220 10% 14%) 0%, hsl(220 12% 10%) 100%)',
                  boxShadow: '0 8px 24px hsl(220 30% 5% / 0.4), inset 0 1px 0 hsl(0 0% 100% / 0.04)',
                }"
              >
                <!-- chrome -->
                <div class="flex items-center gap-1.5 px-3 py-2 border-b border-zinc-800/80">
                  <span class="w-2 h-2 rounded-full bg-zinc-600" />
                  <span class="w-2 h-2 rounded-full bg-zinc-600" />
                  <span class="w-2 h-2 rounded-full bg-zinc-600" />
                  <span class="ml-2 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">install</span>
                </div>
                <!-- body -->
                <div class="p-3 font-mono text-[11px] leading-relaxed">
                  <p class="text-zinc-400">
                    <span class="text-emerald-400">$</span>
                    msiexec /i
                    <span class="text-zinc-200">FenixGuardianInstaller.msi</span>
                    /quiet
                  </p>
                  <p class="text-zinc-500 mt-1">→ deploying to 22 endpoints…</p>
                  <p class="text-emerald-400 mt-1 flex items-center gap-1.5">
                    <span class="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    22 / 22 installed · 4.2s
                  </p>
                </div>
              </div>

              <!-- Step 2 — agent status panel with offline-recovery moment.
                   Top line: agent identity + CPU + green dot — "it runs silent".
                   Connectivity strip with a visible gap + "recuperado · 47 eventos
                   enviados" — "it survived a Wi-Fi drop". Two messages in one
                   compact panel, both concrete and verifiable. -->
              <div
                v-else-if="step.mockup === 'telemetry'"
                class="w-full rounded-lg border border-border bg-card p-3.5 flex flex-col"
                :style="{ boxShadow: '0 6px 18px hsl(214 28% 30% / 0.06), inset 0 1px 0 hsl(0 0% 100% / 0.5)' }"
              >
                <!-- Agent identity row — speaks to "silent" -->
                <div class="flex items-center justify-between mb-2.5">
                  <p class="text-[11px] font-semibold text-foreground tracking-tight">
                    Fenix Guardian agent
                  </p>
                  <span class="text-[9px] font-mono text-muted-foreground">v0.1.7</span>
                </div>
                <div class="flex items-center gap-3 text-[10px] font-mono text-muted-foreground mb-3">
                  <span class="inline-flex items-center gap-1">
                    <span class="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    activo
                  </span>
                  <span>CPU 0.2 %</span>
                  <span>RAM 14 MB</span>
                </div>

                <!-- Connectivity strip with offline gap. Uses emerald
                     (the canonical "up" colour in monitoring tools) so
                     the strip and the recovery line don't add to the
                     cyan footprint of the section. -->
                <p class="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                  Conexión · última hora
                </p>
                <div class="flex items-center gap-[2px] h-2 mb-1.5">
                  <span
                    v-for="(c, i) in connectivity"
                    :key="i"
                    class="flex-1 h-full rounded-sm"
                    :style="{
                      background: c === 1
                        ? 'hsl(142 71% 45% / 0.8)'
                        : 'hsl(var(--muted-foreground) / 0.25)',
                    }"
                  />
                </div>
                <div class="flex justify-between text-[9px] font-mono text-muted-foreground/70">
                  <span>−60 min</span>
                  <span>ahora</span>
                </div>

                <!-- Recovery summary -->
                <div class="mt-auto pt-2 flex items-center gap-1.5 text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                  <span aria-hidden="true">↻</span>
                  <span>recuperado · 47 eventos enviados</span>
                </div>
              </div>

              <!-- Step 3 — comparative KPI tile: two teams, productivity bars,
                   gap headline. The kind of view a manager screenshots to make
                   their case. "Decision with real numbers", not "rule configured". -->
              <div
                v-else
                class="w-full rounded-lg border border-border bg-card p-3.5 flex flex-col"
                :style="{ boxShadow: '0 6px 18px hsl(214 28% 30% / 0.06), inset 0 1px 0 hsl(0 0% 100% / 0.5)' }"
              >
                <div class="flex items-center justify-between mb-2.5">
                  <p class="text-[11px] font-semibold text-foreground tracking-tight">
                    Soporte vs Ventas
                  </p>
                  <span class="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
                    esta semana
                  </span>
                </div>

                <!-- Soporte bar — emerald (good / above target).
                     Step 3 uses the canonical KPI-traffic-light palette
                     (green = healthy, amber = warning) instead of cyan,
                     so the section's cyan footprint drops to just the
                     eyebrow + rail dots. -->
                <div class="mb-2">
                  <div class="flex items-baseline justify-between mb-1">
                    <span class="text-[10px] text-foreground/80">Soporte</span>
                    <span class="text-[11px] font-mono font-bold tabular-nums text-foreground">87 %</span>
                  </div>
                  <div class="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      class="h-full rounded-full"
                      style="width: 87%; background: hsl(142 71% 45%);"
                    />
                  </div>
                </div>

                <!-- Ventas bar — amber (warning / underperforming).
                     Pairs visually with the gap headline below. -->
                <div class="mb-3">
                  <div class="flex items-baseline justify-between mb-1">
                    <span class="text-[10px] text-foreground/80">Ventas</span>
                    <span class="text-[11px] font-mono font-bold tabular-nums text-foreground">64 %</span>
                  </div>
                  <div class="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      class="h-full rounded-full"
                      style="width: 64%; background: hsl(38 92% 50%);"
                    />
                  </div>
                </div>

                <!-- Gap headline — amber, paired with the Ventas bar.
                     This is the "alert worth seeing" framing: the gap
                     IS the signal a manager would act on. -->
                <div class="mt-auto pt-2 border-t border-dashed border-border">
                  <p class="flex items-baseline gap-1.5">
                    <span class="text-base font-bold tabular-nums text-amber-600 dark:text-amber-400">+23 pp</span>
                    <span class="text-[10px] text-muted-foreground">brecha · misma plantilla</span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Big mono timestamp — editorial anchor in neutral so the
                 cyan accent stays reserved for the rail dots below and
                 the gap headline of step 3. -->
            <p class="font-mono text-3xl lg:text-4xl font-bold tabular-nums text-foreground leading-none mb-3 tracking-tight">
              {{ step.time }}
            </p>

            <h3 class="text-xl font-bold text-foreground mb-2 tracking-tight">
              {{ step.title }}
            </h3>

            <p class="text-sm text-muted-foreground leading-relaxed">
              {{ step.description }}
            </p>
          </div>
        </AnimatedSection>
      </div>

      <!-- Timeline rail: 3 dots aligned under the columns above + a soft
           hairline connecting them. The rail is the only element that
           ties the section together visually after we dropped the
           "Step N" badges. -->
      <div class="relative max-w-5xl mx-auto mt-14 hidden md:block">
        <div class="grid grid-cols-3 relative">
          <!-- Connecting hairline runs between the outer dots only,
               so it doesn't bleed past the section's content width.
               left/right at 16.66% put the line endpoints at the column
               centres. -->
          <div class="absolute left-[16.66%] right-[16.66%] top-1.5 h-px bg-gradient-to-r from-accent/30 via-accent/55 to-accent/30" />
          <div v-for="(_, i) in 3" :key="i" class="flex justify-center">
            <span
              class="relative w-3 h-3 rounded-full bg-accent"
              :style="{ boxShadow: '0 0 0 4px hsl(var(--background)), 0 0 12px hsl(var(--accent) / 0.5)' }"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
